<script setup lang="ts" generic="T, C extends ColumnModel, R extends RecordMap<C>, F extends Record<string, unknown>">
import { api } from "@/api";
import { PV } from "@/widgets";
import { useDebounceFn } from "@vueuse/core";
import { delay, isNotNil, isString } from "es-toolkit";
import type { DataTablePageEvent } from "primevue";
import { nextTick, ref, watch } from "vue";
import type { ColumnModel, RecordMap, defineTableSource } from "../common";

const props = defineProps<{
    table: ReturnType<typeof defineTableSource<T, C, R, F>>;
    columns: C[];
    filters: F;
    attachRefresh?: (fn: () => Promise<void>) => void;
}>();

const emits = defineEmits<{
    search: [];
    update: [data: R[]];
}>();

const slots = defineSlots<{
    headers: (props: { refresh: () => Promise<void> }) => any;
    default: () => any;
}>();

const pageModel = defineModel<number>("page", {
    default: 0,
    required: false,
});

const rowsModel = defineModel<number>("rows", {
    default: 10,
    required: false,
});

const searchModel = defineModel<string | undefined>("search", {
    required: false,
});

const loading = ref(false);
const records = ref<R[]>([]);
const totalRecords = ref(0);
const hasError = ref(false);

async function refresh() {
    const params = {
        pagination: {
            page: pageModel.value,
            rows: rowsModel.value,
        },
        search: searchModel.value,
        filters: props.filters,
    };

    emits("search");
    loading.value = true;
    hasError.value = false;
    records.value = [...new Array(params.pagination.rows)];
    await nextTick();
    const startMs = Date.now();

    const src = props.table.src;
    let data: { records: T[]; totalRecords: number } | undefined;
    if (isString(src)) {
        data = await api.raw.post<typeof params, typeof data>(src, params).execute();
    } else {
        data = await src(params).execute();
    }

    const doneMs = Date.now();
    if (import.meta.env.DEV && doneMs - startMs < 250) await delay(250 - (doneMs - startMs));
    loading.value = false;

    if (data === undefined) {
        records.value = [];
        totalRecords.value = 0;
        hasError.value = true;
        emits("update", []);
        return;
    }

    records.value = data.records.map(props.table.mapping);
    totalRecords.value = data.totalRecords;
    emits("update", records.value as R[]);
}

async function onPageChange(event: DataTablePageEvent) {
    pageModel.value = event.page;
    rowsModel.value = event.rows;
}

const refreshFn = useDebounceFn(
    async () => {
        await refresh();
    },
    500,
    {
        maxWait: 2000,
        rejectOnCancel: false,
    },
);

watch(
    () => props.attachRefresh,
    () => {
        if (isNotNil(props.attachRefresh)) {
            props.attachRefresh(refresh);
        }
    },
    { immediate: true },
);

watch([searchModel], refreshFn);
watch([pageModel, rowsModel], refresh, { immediate: true });
</script>

<template>
    <PV.DataTable
        class="rns-table"
        currentPageReportTemplate="Menampilkan {first} - {last} dari {totalRecords} entri"
        :first="pageModel * rowsModel"
        lazy
        paginator
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
        :pt="{ bodyRow: 'hover:bg-gray-100!' }"
        reorderableColumns
        :rows="rowsModel"
        :rowsPerPageOptions="[10, 25, 50]"
        scrollable
        scrollHeight="flex"
        stripedRows
        :totalRecords="totalRecords"
        :value="records"
        :virtualScrollerOptions="records.length > 25 ? { itemSize: 42 } : undefined"
        @page="onPageChange"
    >
        <template v-if="slots.headers !== undefined" #header>
            <slot name="headers" :refresh="refresh"></slot>
        </template>

        <template v-if="loading">
            <template v-for="column in props.columns">
                <PV.Column :header="column.title">
                    <template #body>
                        <PV.Skeleton />
                    </template>
                </PV.Column>
            </template>
        </template>
        <template v-else>
            <slot name="default"></slot>
        </template>

        <template #empty>
            <div class="flex items-center justify-center h-[calc(100vh/2)]">
                <template v-if="hasError">Terjadi kesalahan.</template>
                <template v-else>Tidak ada data.</template>
            </div>
        </template>
    </PV.DataTable>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-table :deep(.p-datatable-thead) {
    @apply shadow-sm;
}
</style>
